import React, { useState, useEffect } from "react";
import theme from "../../data/theme";
import { draftCountdown } from "../../utils/liveBoard";

/**
 * DraftCountdown — how long until the auction, on whichever screen you have.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 IT RENDERS THE VIEWER'S OWN CLOCK FROM A SHARED INSTANT. `board.draftAt`
 * is epoch ms — one number, published to everybody — and `toLocaleString` turns
 * it into whatever the wall says where the reader is. That is the whole point:
 * Ben types 7pm in New York, somebody opening the link in another zone sees
 * their own correct hour rather than a "7:00 PM" that means nothing to them.
 *
 * 🔴 IT GETS OUT OF THE WAY. `draftCountdown` returns null once the first sale
 * lands, so this stops existing for the rest of the night rather than sitting
 * in a corner counting up at a room that has moved on. The window between the
 * scheduled time and pick one reads DRAFTING NOW — a count-up is noise and a
 * negative number reads as a bug to everyone who sees it.
 *
 * 🔴 DISPLAY ONLY, exactly like the nomination clock. The interval here forces
 * a re-render and decides nothing; nothing is committed, started, unlocked or
 * auto-anything, and it stops the moment there is nothing left to count.
 *
 * Presentational: every input is a prop, so /watch renders it outside
 * PlayerCardProvider like everything else on that page.
 * ══════════════════════════════════════════════════════════════════════
 */
const TICK_MS = 1000;

/** The parts, recomputed on a ticker that stops as soon as it is pointless. */
export function useDraftCountdown(board) {
  const [now, setNow] = useState(() => Date.now());
  const live = draftCountdown(board, now);
  // Only a pending countdown changes on its own. "Now" is static text and a
  // finished one renders nothing, so neither needs a heartbeat.
  const running = live?.state === "pending";

  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(t);
  }, [running]);

  return live;
}

/** "2d 3h 14m" — seconds only once they are the interesting part. */
function humanize(c) {
  if (c.days > 0) return `${c.days}d ${c.hours}h ${c.minutes}m`;
  if (c.hours > 0) return `${c.hours}h ${c.minutes}m`;
  if (c.minutes > 0) return `${c.minutes}m ${String(c.seconds).padStart(2, "0")}s`;
  return `${c.seconds}s`;
}

export default function DraftCountdown({ board, compact = false }) {
  const c = useDraftCountdown(board);
  if (!c) return null;

  const drafting = c.state === "now";
  // The absolute time, in the reader's zone. Kept alongside the relative
  // countdown because "2d 3h" alone does not tell anyone what to put in a
  // calendar, and the zone question is exactly what this feature is about.
  const local = new Date(c.at).toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });

  return (
    <div
      data-testid="draft-countdown"
      style={{
        display: "flex", alignItems: "baseline", gap: compact ? 10 : 14, flexWrap: "wrap",
        background: drafting ? "rgba(34,197,94,0.10)" : theme.colors.bg.surface,
        border: `1px solid ${drafting ? "rgba(34,197,94,0.45)" : theme.colors.border.subtle}`,
        borderRadius: theme.radius.xl,
        padding: compact ? "8px 14px" : "14px 18px",
        marginBottom: 12,
      }}
    >
      <span style={{
        fontSize: theme.fontSize.caption, letterSpacing: "0.22em", textTransform: "uppercase",
        color: drafting ? "#4ade80" : theme.colors.text.faint, fontWeight: theme.fontWeight.heavy,
      }}>{drafting ? "Drafting now" : "Draft starts in"}</span>

      {!drafting && (
        <span style={{
          fontFamily: theme.fonts.display, fontWeight: theme.fontWeight.black,
          fontSize: compact ? "clamp(18px, 2.4vw, 28px)" : "clamp(22px, 3.4vw, 40px)",
          lineHeight: 1, color: theme.colors.brand.accent,
          fontVariantNumeric: "tabular-nums", letterSpacing: "-0.5px",
        }}>{humanize(c)}</span>
      )}

      <span style={{
        fontSize: theme.fontSize.caption, color: theme.colors.text.muted,
        fontWeight: theme.fontWeight.bold,
      }}>{local}</span>
    </div>
  );
}
